<template>
  <form @submit.prevent="handleSubmit" class="activity-log-form">
    <!-- Program selector (shown when no program is pre-selected by parent) -->
    <div v-if="!fixedProgram" class="form-field">
      <IntercomSelect
        v-model="formData.program_id"
        :label="$t('activityLog.program')"
        :options="programOptions"
        :placeholder="$t('activityLog.selectProgram')"
        required
        :disabled="loading"
        @update:model-value="handleProgramChange"
      />
    </div>
    <div v-else class="form-field form-field--readonly">
      <span class="field-label">{{ $t('activityLog.program') }}</span>
      <span class="readonly-value">{{ fixedProgram.name }}</span>
    </div>

    <div class="form-field">
      <IntercomDateInput
        v-model="formData.activity_date"
        :label="$t('activityLog.date')"
        required
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomInput
        v-model.number="formData.metric_value"
        :label="activeMetricLabel"
        type="number"
        placeholder="0"
        required
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomInput
        v-model.number="formData.total_expense"
        :label="$t('activityLog.totalExpense')"
        type="number"
        placeholder="0.00"
        :disabled="loading"
      />
      <p class="field-hint">{{ $t('activityLog.expenseHint') }}</p>
    </div>

    <!-- Live cost-per-unit preview -->
    <div v-if="formData.metric_value > 0 && formData.total_expense > 0" class="cost-preview">
      <span class="cost-label">{{ $t('activityLog.costPerUnit', { unit: activeMetricUnit }) }}:</span>
      <span class="cost-value">{{ formatCompactCurrency(costPerUnit) }}</span>
    </div>

    <div class="form-field">
      <IntercomInput
        v-model="formData.notes"
        :label="$t('activityLog.notes')"
        placeholder="Optional notes"
        :disabled="loading"
      />
    </div>

    <div class="form-actions">
      <IntercomButton type="button" variant="ghost" @click="$emit('cancel')" :disabled="loading">
        {{ $t('common.cancel') }}
      </IntercomButton>
      <IntercomButton type="submit" variant="primary" :loading="loading">
        {{ $t('common.save') }}
      </IntercomButton>
    </div>
  </form>
</template>

<script setup>
import { reactive, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import { formatCompactCurrency } from '@shared/utils/currency.js'
import {
  IntercomInput,
  IntercomDateInput,
  IntercomSelect,
  IntercomButton,
} from '@shared/components/intercom'

const { t } = useI18n()

const props = defineProps({
  entry: { type: Object, default: null },
  date: { type: String, default: null },
  programs: { type: Array, default: () => [] },
  // When a program is already selected at the view level, pass it here
  // to lock the selector and show dynamic label
  fixedProgram: { type: Object, default: null },
  loading: { type: Boolean, default: false },
})

const emit = defineEmits(['submit', 'cancel'])

const formData = reactive({
  program_id: props.fixedProgram?.id || null,
  activity_date: props.date || dayjs().format('YYYY-MM-DD'),
  metric_value: null,
  total_expense: null,
  notes: '',
})

// The currently selected program object (either fixed or picked from dropdown)
const selectedProgram = computed(() => {
  if (props.fixedProgram) return props.fixedProgram
  return props.programs.find(p => p.id === formData.program_id) || null
})

const activeMetricLabel = computed(() => {
  return selectedProgram.value?.metric_label || t('activityLog.metricValue')
})

const activeMetricUnit = computed(() => {
  return selectedProgram.value?.metric_unit || 'units'
})

const costPerUnit = computed(() => {
  if (!formData.metric_value || !formData.total_expense) return 0
  return formData.total_expense / formData.metric_value
})

const programOptions = computed(() =>
  props.programs
    .filter(p => p.is_active)
    .map(p => ({ value: p.id, label: p.name }))
)

const handleProgramChange = () => {
  // metric_value label updates reactively via selectedProgram computed
}

watch(() => props.entry, (newEntry) => {
  if (newEntry) {
    formData.program_id = newEntry.program_id || props.fixedProgram?.id || null
    formData.activity_date = newEntry.activity_date
      ? dayjs(newEntry.activity_date).format('YYYY-MM-DD')
      : dayjs().format('YYYY-MM-DD')
    formData.metric_value = newEntry.metric_value || null
    formData.total_expense = newEntry.total_expense || null
    formData.notes = newEntry.notes || ''
  } else {
    formData.program_id = props.fixedProgram?.id || null
    formData.activity_date = props.date || dayjs().format('YYYY-MM-DD')
    formData.metric_value = null
    formData.total_expense = null
    formData.notes = ''
  }
}, { immediate: true })

watch(() => props.date, (newDate) => {
  if (newDate && !props.entry) {
    formData.activity_date = newDate
  }
})

watch(() => props.fixedProgram, (newProgram) => {
  if (newProgram) {
    formData.program_id = newProgram.id
  }
})

const handleSubmit = () => {
  emit('submit', { ...formData })
}
</script>

<style scoped>
.activity-log-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
}

.form-field--readonly {
  gap: var(--space-1);
}

.field-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
}

.readonly-value {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
}

.field-hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
}

.cost-preview {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3);
  background: var(--color-gray-50);
  border-radius: var(--radius-md);
}

.cost-label {
  color: var(--color-gray-600);
  font-size: var(--font-size-sm);
}

.cost-value {
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
}
</style>
